import { html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";

import "@phosphor-icons/webcomponents/PhPlus";
import "ui-components/button/wow-button";
import "ui-components/confirmation/wow-confirmation";
import "ui-components/empty-state/empty-state";

import { styles } from "./live-events-page.css";
import { SignalElement, inject } from "ui-common/di";
import type { IAuthState } from "ui-common/services/auth/types";
import type { ICreatorLiveEventsState, ICreatorLiveEventsService } from "../../services/creator-live-events/types";
import type { ILiveEventFormService } from "ui-common/services/live-event-form/types";
import { featureFlagsQueryOptions } from "../../queries/feature-flags";
import { DataQueryController } from "ui-common/data-query-controller";
import { rpc } from "ui-common/services/rpc";
import { NotificationService } from "ui-components/notification";
import type { LiveEvent } from "api-contracts/rpc/get-live-events";

import "../../components/page-content/page-content";
import "../../components/view-primary-buttons/view-primary-buttons";
import "../../components/share-content-modal/share-content-modal";
import "../../components/upcoming-event-banner/upcoming-event-banner";
import "../../components/live-events-table/live-events-table";
import type { ShareContentDetails } from "../../components/share-content-modal/share-content-modal";
import { t, translate } from "../../translations/i18n";
import { navigateTo } from "../../router";

type EventFilter = "all" | "scheduled" | "ended" | "archived";

@customElement("live-events-page")
export class LiveEventsPage extends SignalElement {
  @inject<IAuthState>("authState")
  private authState!: IAuthState;

  @inject<ICreatorLiveEventsState>("creatorLiveEventsState")
  private liveEventsState!: ICreatorLiveEventsState;

  @inject<ICreatorLiveEventsService>("creatorLiveEventsService")
  private liveEventsService!: ICreatorLiveEventsService;

  @inject<ILiveEventFormService>("liveEventFormService")
  private formService!: ILiveEventFormService;

  static override styles = [styles];

  @state()
  private selectedFilter: EventFilter = "all";

  @state()
  private deletingEventId?: string;

  @state()
  private archivingEventId?: string;

  @state()
  private stoppingEventId?: string;

  @state()
  private endingEventId?: string;

  @state()
  private showShareModal = false;

  @state()
  private shareContentDetails?: ShareContentDetails;

  @state()
  private goLiveEventId?: string;

  private featureFlagsQuery = new DataQueryController(this, () => featureFlagsQueryOptions);

  private handleFilterChange(e: Event) {
    this.selectedFilter = (e.target as HTMLSelectElement).value as EventFilter;
  }

  private handleCreateClick() {
    navigateTo("live-events/create");
  }

  private handleEditClick(event: LiveEvent) {
    this.formService.reset();
    navigateTo("live-events/edit", true, [event.id]);
  }

  private handleDeleteClick(eventId: string) {
    this.deletingEventId = eventId;
  }

  private handleCancelDelete() {
    this.deletingEventId = undefined;
  }

  private async handleConfirmDelete() {
    if (!this.deletingEventId) return;

    try {
      await rpc("delete-live-event", { event_id: this.deletingEventId });
      NotificationService.success(translate("liveEventsPage.eventDeletedSuccess"));
      this.deletingEventId = undefined;
      this.liveEventsService.refetch();
    } catch (error) {
      console.error("Error deleting event:", error);
      NotificationService.error(translate("liveEventsPage.eventDeletedError"));
    }
  }

  private handleArchiveClick(eventId: string) {
    this.archivingEventId = eventId;
  }

  private handleCancelArchive() {
    this.archivingEventId = undefined;
  }

  private async handleConfirmArchive() {
    if (!this.archivingEventId) return;

    try {
      await rpc("archive-live-event", { event_id: this.archivingEventId });
      NotificationService.success(translate("liveEventsPage.eventArchivedSuccess"));
      this.archivingEventId = undefined;
      this.liveEventsService.refetch();
    } catch (error) {
      console.error("Error archiving event:", error);
      NotificationService.error(translate("liveEventsPage.eventArchivedError"));
    }
  }

  private handleStopLiveClick(eventId: string) {
    this.stoppingEventId = eventId;
  }

  private handleCancelStopLive() {
    this.stoppingEventId = undefined;
  }

  private async handleConfirmStopLive() {
    if (!this.stoppingEventId) return;

    try {
      await rpc("stop-live-event", { event_id: this.stoppingEventId });
      NotificationService.success(translate("liveEventsPage.streamPausedSuccess"));
      this.stoppingEventId = undefined;
      this.liveEventsService.refetch();
    } catch (error) {
      console.error("Error stopping live event:", error);
      NotificationService.error(translate("liveEventsPage.streamPausedError"));
    }
  }

  private handleEndEventClick(eventId: string) {
    this.endingEventId = eventId;
  }

  private handleCancelEndEvent() {
    this.endingEventId = undefined;
  }

  private async handleConfirmEndEvent() {
    if (!this.endingEventId) return;

    try {
      await rpc("end-live-event", { event_id: this.endingEventId });
      NotificationService.success(translate("liveEventsPage.eventEndedSuccess"));
      this.endingEventId = undefined;
      this.liveEventsService.refetch();
    } catch (error) {
      console.error("Error ending event:", error);
      NotificationService.error(translate("liveEventsPage.eventEndedError"));
    }
  }

  private handleEventCreated(e: CustomEvent<{ event?: { id: string; title: string; thumbnail_url?: string | null } }>) {
    this.liveEventsService.refetch();

    if (e.detail?.event) {
      this.shareContentDetails = this.buildShareContentDetails(
        e.detail.event.id,
        e.detail.event.title,
        e.detail.event.thumbnail_url,
      );
      this.showShareModal = true;
    }
  }

  private handleShareModalClose() {
    this.showShareModal = false;
    this.shareContentDetails = undefined;
  }

  private handleShareClick(event: LiveEvent) {
    this.shareContentDetails = this.buildShareContentDetails(event.id, event.title, event.thumbnail_url);
    this.showShareModal = true;
  }

  private buildShareContentDetails(id: string, title: string, thumbnail_url?: string | null): ShareContentDetails {
    const isProduction = window.location.hostname.endsWith("worldofwow.com");
    const baseUrl = isProduction ? "https://worldofwow.com" : "https://worldofwow.dev";
    const handle = this.authState.currentUser.get()?.handle ?? "";
    return {
      id,
      title,
      thumbnail_url,
      shareUrl: `${baseUrl}/${handle}/live-events/${id}`,
    };
  }

  private handleStartStreamingClick(eventId: string) {
    navigateTo("live-events/stream", true, [eventId]);
  }

  private handleCancelGoLive() {
    this.goLiveEventId = undefined;
  }

  private async handleConfirmGoLive() {
    if (!this.goLiveEventId) return;

    try {
      await rpc("start-live-event", { event_id: this.goLiveEventId });
      NotificationService.success(translate("liveEventsPage.goLiveSuccess"));
      this.goLiveEventId = undefined;
      this.liveEventsService.refetch();
    } catch (error) {
      console.error("Error going live:", error);
      NotificationService.error(translate("liveEventsPage.goLiveFailed"));
    }
  }

  private handleViewClick() {
    const handle = this.authState.currentUser.get()?.handle;
    if (handle) window.open(`/${handle}/live-events`, "_self", "noopener,noreferrer");
  }

  private handleEventWentLive() {
    this.liveEventsService.refetch();
  }

  private getStatusPriority(status: string): number {
    if (status === "live") return 0;
    if (status === "scheduled") return 1;
    return 2;
  }

  private sortEvents(events: LiveEvent[]): LiveEvent[] {
    return [...events].sort((a, b) => {
      const priorityA = this.getStatusPriority(a.status);
      const priorityB = this.getStatusPriority(b.status);
      if (priorityA !== priorityB) return priorityA - priorityB;

      const timeA = new Date(a.scheduled_start_time).getTime();
      const timeB = new Date(b.scheduled_start_time).getTime();

      // Live/scheduled: closest first (ASC). Ended: most recent first (DESC).
      if (priorityA <= 1) return timeA - timeB;
      return timeB - timeA;
    });
  }

  private getFilteredEvents(events: LiveEvent[]): LiveEvent[] {
    switch (this.selectedFilter) {
      case "scheduled":
        return events.filter((e) => e.status === "scheduled");
      case "ended":
        return events.filter((e) => e.status === "ended");
      case "archived":
        return events.filter((e) => e.status === "archived");
      case "all":
      default:
        return events.filter((e) => e.status !== "archived");
    }
  }

  private getFilterCounts(events: LiveEvent[]) {
    return {
      all: events.filter((e) => e.status !== "archived").length,
      scheduled: events.filter((e) => e.status === "scheduled").length,
      ended: events.filter((e) => e.status === "ended").length,
      archived: events.filter((e) => e.status === "archived").length,
    };
  }

  private getClosestUpcomingEvent(events: LiveEvent[]): LiveEvent | undefined {
    const activeEvents = events.filter((e) => e.status === "live" || e.status === "scheduled");
    if (activeEvents.length === 0) return undefined;

    return activeEvents.reduce((closest, event) => {
      if (event.status === "live" && closest.status !== "live") return event;
      if (closest.status === "live" && event.status !== "live") return closest;
      const closestTime = new Date(closest.scheduled_start_time).getTime();
      const eventTime = new Date(event.scheduled_start_time).getTime();
      return eventTime < closestTime ? event : closest;
    });
  }

  private renderMainContent() {
    const featureFlags = this.featureFlagsQuery.result?.data?.features ?? [];
    const hasLiveEvents = featureFlags.includes("paid-live-events") || featureFlags.includes("open-live-events");

    // Show access denied message for non-enabled creators
    if (!hasLiveEvents) {
      return html` <div class="main-content">${this.renderEmptyState()}</div> `;
    }

    const isLoading = this.liveEventsState.isLoading.get();

    // Show loading state
    if (isLoading) {
      return html`
        <div class="main-content">
          <div class="loading-state">
            <div>${t("liveEventsPage.loadingEvents")}</div>
          </div>
        </div>
      `;
    }

    const events = this.liveEventsState.events.get();

    if (events.length === 0) {
      return html`<div class="main-content">${this.renderEmptyState()}</div>`;
    }

    const closestEvent = this.getClosestUpcomingEvent(events);
    const filteredEvents = this.getFilteredEvents(events);
    const remainingEvents = closestEvent ? filteredEvents.filter((e) => e.id !== closestEvent.id) : filteredEvents;
    const sortedEvents = this.sortEvents(remainingEvents);
    const hasActiveEvents = events.some((e) => e.status === "live" || e.status === "scheduled");
    const counts = this.getFilterCounts(events);

    return html`
      <div class="main-content">
        ${!hasActiveEvents && this.selectedFilter === "all" ? this.renderEmptyState(true) : nothing}
        ${closestEvent
          ? html`
              <div class="upcoming-section">
                <h3 class="section-title">${t("liveEventsPage.upcomingEvent")}</h3>
                <upcoming-event-banner
                  .event=${closestEvent}
                  @stream-action=${this.handleBannerStreamAction}
                  @share-click=${this.handleBannerShareClick}
                  @end-event=${this.handleBannerEndEvent}
                  @edit-event=${this.handleBannerEditEvent}
                  @duplicate-event=${this.handleBannerDuplicateEvent}
                  @delete-event=${this.handleBannerDeleteEvent}
                  @archive-event=${this.handleBannerArchiveEvent}
                  @watch-event=${this.handleBannerWatchEvent}
                ></upcoming-event-banner>
              </div>
            `
          : nothing}
        <div>
          <div class="events-header">
            <h3 class="section-title">${t("liveEventsPage.myEvents")}</h3>
            <div class="events-filter">
              <wow-button
                size="small"
                variant=${this.selectedFilter === "all" ? "primary" : "subtle"}
                @click=${() => (this.selectedFilter = "all")}
              >
                ${t("liveEventsPage.all")} (${counts.all})
              </wow-button>
              <wow-button
                size="small"
                variant=${this.selectedFilter === "scheduled" ? "primary" : "subtle"}
                @click=${() => (this.selectedFilter = "scheduled")}
                ?disabled=${counts.scheduled === 0}
              >
                ${t("liveEventsPage.scheduled")} (${counts.scheduled})
              </wow-button>
              <wow-button
                size="small"
                variant=${this.selectedFilter === "ended" ? "primary" : "subtle"}
                @click=${() => (this.selectedFilter = "ended")}
                ?disabled=${counts.ended === 0}
              >
                ${t("liveEventsPage.ended")} (${counts.ended})
              </wow-button>
              ${counts.archived > 0
                ? html`
                    <wow-button
                      size="small"
                      variant=${this.selectedFilter === "archived" ? "primary" : "subtle"}
                      @click=${() => (this.selectedFilter = "archived")}
                    >
                      ${t("liveEventsPage.archived")} (${counts.archived})
                    </wow-button>
                  `
                : nothing}
            </div>
            <select class="events-filter-mobile" .value=${this.selectedFilter} @change=${this.handleFilterChange}>
              <option value="all">${t("liveEventsPage.all")} (${counts.all})</option>
              <option value="scheduled" ?disabled=${counts.scheduled === 0}>
                ${t("liveEventsPage.scheduled")} (${counts.scheduled})
              </option>
              <option value="ended" ?disabled=${counts.ended === 0}>
                ${t("liveEventsPage.ended")} (${counts.ended})
              </option>
              ${counts.archived > 0
                ? html`<option value="archived">${t("liveEventsPage.archived")} (${counts.archived})</option>`
                : nothing}
            </select>
          </div>
          ${sortedEvents.length > 0
            ? html`
                <live-events-table
                  .events=${sortedEvents}
                  @stream-action=${this.handleBannerStreamAction}
                  @share-click=${this.handleBannerShareClick}
                  @end-event=${this.handleBannerEndEvent}
                  @edit-event=${this.handleBannerEditEvent}
                  @duplicate-event=${this.handleBannerDuplicateEvent}
                  @delete-event=${this.handleBannerDeleteEvent}
                  @archive-event=${this.handleBannerArchiveEvent}
                  @watch-event=${this.handleBannerWatchEvent}
                ></live-events-table>
              `
            : nothing}
        </div>
      </div>
    `;
  }

  private handleBannerStreamAction(e: CustomEvent<{ action: string; eventId: string }>) {
    const { action, eventId } = e.detail;
    const isTouchDevice = window.matchMedia("(hover: none) and (pointer: coarse)").matches;
    if (action === "obs" && isTouchDevice) {
      this.handleStartStreamingClick(eventId);
      return;
    }
    if (action === "browser") {
      this.handleStartStreamingClick(eventId);
    } else if (action === "obs") {
      this.goLiveEventId = eventId;
    } else if (action === "view-results") {
      navigateTo("analytics", true, ["live-events"]);
    }
  }

  private handleBannerShareClick(e: CustomEvent<{ event: LiveEvent }>) {
    this.handleShareClick(e.detail.event);
  }

  private handleBannerStopLive(e: CustomEvent<{ eventId: string }>) {
    this.handleStopLiveClick(e.detail.eventId);
  }

  private handleBannerEndEvent(e: CustomEvent<{ eventId: string }>) {
    this.handleEndEventClick(e.detail.eventId);
  }

  private handleBannerEditEvent(e: CustomEvent<{ event: LiveEvent }>) {
    this.handleEditClick(e.detail.event);
  }

  private async handleBannerDuplicateEvent(e: CustomEvent<{ event: LiveEvent }>) {
    await this.formService.duplicateEvent(e.detail.event.id);
    navigateTo("live-events/create");
  }

  private handleBannerWatchEvent(e: CustomEvent<{ event: LiveEvent }>) {
    const handle = this.authState.currentUser.get()?.handle;
    if (handle) {
      window.open(`/${handle}/live-events/${e.detail.event.id}`, "_blank", "noopener,noreferrer");
    }
  }

  private handleBannerDeleteEvent(e: CustomEvent<{ eventId: string }>) {
    this.handleDeleteClick(e.detail.eventId);
  }

  private handleBannerArchiveEvent(e: CustomEvent<{ eventId: string }>) {
    this.handleArchiveClick(e.detail.eventId);
  }

  private renderEmptyState(hadPreviousEvents = false) {
    const featureFlags = this.featureFlagsQuery.result?.data?.features ?? [];
    const hasLiveEvents = featureFlags.includes("paid-live-events") || featureFlags.includes("open-live-events");

    if (!hasLiveEvents) {
      return html`
        <wow-empty-state
          headline=${t("liveEventsPage.notAvailableTitle")}
          subtitle=${t("liveEventsPage.notAvailableMessage")}
        >
          <ph-plus slot="icon" size="24"></ph-plus>
        </wow-empty-state>
      `;
    }

    if (hadPreviousEvents) {
      return html`
        <div>
          <h3 class="section-title">${t("liveEventsPage.nextEventTitle")}</h3>
          <div class="no-active-events-banner">
            <wow-empty-state
              headline=${t("liveEventsPage.newEventTitle")}
              subtitle=${t("liveEventsPage.newEventSubtitle")}
              button-text=${t("liveEventsPage.createLiveEvent")}
              @cta-click=${this.handleCreateClick}
            >
              <ph-plus slot="icon" size="24"></ph-plus>
            </wow-empty-state>
          </div>
        </div>
      `;
    }

    return html`
      <div>
        <h3 class="section-title">${t("liveEventsPage.myEvents")}</h3>
        <wow-empty-state
          headline=${t("liveEventsPage.noLiveEventsYet")}
          subtitle=${t("liveEventsPage.createFirstLiveEvent")}
          button-text=${t("liveEventsPage.createLiveEvent")}
          @cta-click=${this.handleCreateClick}
        >
          <ph-plus slot="icon" size="24"></ph-plus>
        </wow-empty-state>
      </div>
    `;
  }

  override render() {
    return html`
      <page-content .pageTitle=${translate("liveEventsPage.pageTitle")}>
        <div slot="actions">
          <view-primary-buttons
            primaryText=${t("liveEventsPage.createLiveEvent")}
            @primary-click=${this.handleCreateClick}
            @view-click=${this.handleViewClick}
          ></view-primary-buttons>
        </div>

        <div class="containers">
          <div class="live-events-content">${this.renderMainContent()}</div>
        </div>
      </page-content>

      ${this.deletingEventId
        ? html`
            <wow-confirmation header=${translate("liveEventsPage.deleteEvent")}>
              <div class="delete-confirmation-content">
                <p>${t("liveEventsPage.deleteConfirmation")}</p>
                <div class="confirmation-buttons">
                  <wow-button variant="danger" size="medium" @click=${this.handleConfirmDelete}>
                    ${t("liveEventsPage.deleteEvent")}
                  </wow-button>
                  <wow-button variant="neutral" size="medium" @click=${this.handleCancelDelete}>
                    ${t("liveEventsPage.cancel")}
                  </wow-button>
                </div>
              </div>
            </wow-confirmation>
          `
        : nothing}
      ${this.archivingEventId
        ? html`
            <wow-confirmation header=${translate("liveEventsPage.archiveEvent")}>
              <div class="delete-confirmation-content">
                <p>${t("liveEventsPage.archiveConfirmation")}</p>
                <div class="confirmation-buttons">
                  <wow-button variant="danger" size="medium" @click=${this.handleConfirmArchive}>
                    ${t("liveEventsPage.archiveEvent")}
                  </wow-button>
                  <wow-button variant="neutral" size="medium" @click=${this.handleCancelArchive}>
                    ${t("liveEventsPage.cancel")}
                  </wow-button>
                </div>
              </div>
            </wow-confirmation>
          `
        : nothing}
      ${this.stoppingEventId
        ? html`
            <wow-confirmation header=${translate("liveEventsPage.pauseStreamHeader")}>
              <div class="delete-confirmation-content">
                <p>${t("liveEventsPage.pauseStreamMessage")}</p>
                <div class="confirmation-buttons">
                  <wow-button variant="danger" size="medium" @click=${this.handleConfirmStopLive}>
                    ${t("liveEventsPage.pauseStreamButton")}
                  </wow-button>
                  <wow-button variant="neutral" size="medium" @click=${this.handleCancelStopLive}>
                    ${t("liveEventsPage.cancel")}
                  </wow-button>
                </div>
              </div>
            </wow-confirmation>
          `
        : nothing}
      ${this.endingEventId
        ? html`
            <wow-confirmation header=${translate("liveEventsPage.endEventHeader")}>
              <div class="delete-confirmation-content">
                <p>${t("liveEventsPage.endEventMessage")}</p>
                <div class="confirmation-buttons">
                  <wow-button variant="danger" size="medium" @click=${this.handleConfirmEndEvent}>
                    ${t("liveEventsPage.endEvent")}
                  </wow-button>
                  <wow-button variant="neutral" size="medium" @click=${this.handleCancelEndEvent}>
                    ${t("liveEventsPage.cancel")}
                  </wow-button>
                </div>
              </div>
            </wow-confirmation>
          `
        : nothing}
      ${this.goLiveEventId
        ? html`
            <wow-confirmation header=${translate("liveEventsPage.goLiveHeader")}>
              <div class="delete-confirmation-content">
                <p>${t("liveEventsPage.goLiveConfirm")}</p>
                <div class="confirmation-buttons">
                  <wow-button variant="danger" size="medium" @click=${this.handleConfirmGoLive}>
                    ${t("liveEventsPage.goLiveButton")}
                  </wow-button>
                  <wow-button variant="neutral" size="medium" @click=${this.handleCancelGoLive}>
                    ${t("liveEventsPage.cancel")}
                  </wow-button>
                </div>
              </div>
            </wow-confirmation>
          `
        : nothing}
      ${this.showShareModal && this.shareContentDetails
        ? html`
            <share-content-modal
              .content=${this.shareContentDetails}
              @modal-close=${this.handleShareModalClose}
            ></share-content-modal>
          `
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "live-events-page": LiveEventsPage;
  }
}
